import React, { useState } from 'react';
import { Plus, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLineActionCall, useLineRead, type Obj } from '@/client/api/whatsapp/line-actions';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  ActionButton,
  areaCls,
  asArray,
  ConfirmDialog,
  Section,
  selectCls,
  toastError,
  recipientOf,
} from './shared';

type Kind =
  | 'text' | 'media' | 'audio' | 'ptv' | 'sticker' | 'location' | 'contact'
  | 'poll' | 'list' | 'buttons' | 'carousel' | 'status';

const KINDS: { id: Kind; label: string; action: string }[] = [
  { id: 'text', label: 'Text', action: 'message.sendText' },
  { id: 'media', label: 'Media (image, video, document, audio)', action: 'message.sendMedia' },
  { id: 'audio', label: 'Voice note', action: 'message.sendWhatsAppAudio' },
  { id: 'ptv', label: 'Video note', action: 'message.sendPtv' },
  { id: 'sticker', label: 'Sticker', action: 'message.sendSticker' },
  { id: 'location', label: 'Location', action: 'message.sendLocation' },
  { id: 'contact', label: 'Contact card', action: 'message.sendContact' },
  { id: 'poll', label: 'Poll', action: 'message.sendPoll' },
  { id: 'list', label: 'List', action: 'message.sendList' },
  { id: 'buttons', label: 'Buttons', action: 'message.sendButtons' },
  { id: 'carousel', label: 'Carousel', action: 'message.sendCarousel' },
  { id: 'status', label: 'Status post (no recipient)', action: 'message.sendStatus' },
];

type Btn = { type: 'reply' | 'url' | 'call' | 'copy'; text: string; value: string };
const btnBody = (b: Btn): Obj => {
  const base: Obj = { type: b.type, displayText: b.text };
  if (b.type === 'reply') base.id = b.value || b.text;
  if (b.type === 'url') base.url = b.value;
  if (b.type === 'call') base.phoneNumber = b.value;
  if (b.type === 'copy') base.copyCode = b.value;
  return base;
};

const ButtonsEditor: React.FC<{ value: Btn[]; onChange: (v: Btn[]) => void; max: number }> = ({ value, onChange, max }) => (
  <div className="space-y-2">
    {value.map((b, i) => (
      <div key={i} className="grid grid-cols-[110px_1fr_1fr_auto] gap-2">
        <select
          className={selectCls}
          value={b.type}
          onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, type: e.target.value as Btn['type'] } : x)))}
        >
          <option value="reply">Reply</option>
          <option value="url">Open link</option>
          <option value="call">Call</option>
          <option value="copy">Copy code</option>
        </select>
        <Input placeholder="Label" value={b.text} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
        <Input
          placeholder={b.type === 'url' ? 'https://' : b.type === 'call' ? 'Phone' : b.type === 'copy' ? 'Code' : 'Reply id (optional)'}
          value={b.value}
          onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
        />
        <Button type="button" variant="ghost" size="icon" aria-label="Remove button" onClick={() => onChange(value.filter((_, j) => j !== i))}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    ))}
    {value.length < max && (
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, { type: 'reply', text: '', value: '' }])}>
        <Plus /> Add button
      </Button>
    )}
  </div>
);

type Card = { title: string; body: string; imageUrl: string; buttons: Btn[] };

const lines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);

const SendTab: React.FC<{ instance: string; connected: boolean }> = ({ instance, connected }) => {
  const call = useLineActionCall();
  const groups = useLineRead(instance, 'group.fetchAllGroups', { query: { getParticipants: 'false' }, enabled: connected });
  const groupList = asArray<{ id: string; subject: string }>(groups.data);

  const [kind, setKind] = useState<Kind>('text');
  const [to, setTo] = useState('');
  const [f, setF] = useState<Record<string, string>>({ mediatype: 'image', stype: 'text', selectable: '1', allContacts: 'true' });
  const [btns, setBtns] = useState<Btn[]>([{ type: 'reply', text: '', value: '' }]);
  const [cards, setCards] = useState<Card[]>([{ title: '', body: '', imageUrl: '', buttons: [{ type: 'url', text: '', value: '' }] }]);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<Obj | null>(null);

  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }));
  const field = (k: string, label: string, ph = '', area = false) => (
    <div>
      <Label>{label}</Label>
      {area ? (
        <textarea className={areaCls} rows={3} placeholder={ph} value={f[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
      ) : (
        <Input placeholder={ph} value={f[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
      )}
    </div>
  );

  const def = KINDS.find((k) => k.id === kind)!;
  const isStatus = kind === 'status';
  const number = recipientOf(to);

  /** Builds the Evolution body, or returns the reason it is not ready. */
  const build = (): { body: Obj } | { error: string } => {
    if (!isStatus && number.length < 8 && !number.endsWith('@g.us')) return { error: 'Enter a phone number with country code, or pick a group' };
    const n: Obj = isStatus ? {} : { number };
    switch (kind) {
      case 'text':
        return f.text?.trim() ? { body: { ...n, text: f.text } } : { error: 'Write the message text' };
      case 'media':
        if (!f.media?.trim()) return { error: 'Enter the media URL' };
        if (f.mediatype === 'document' && !f.fileName?.trim()) return { error: 'Documents need a file name' };
        return { body: { ...n, mediatype: f.mediatype, media: f.media.trim(), caption: f.caption || undefined, fileName: f.fileName || undefined } };
      case 'audio':
        return f.url?.trim() ? { body: { ...n, audio: f.url.trim() } } : { error: 'Enter the audio URL' };
      case 'ptv':
        return f.url?.trim() ? { body: { ...n, video: f.url.trim() } } : { error: 'Enter the video URL' };
      case 'sticker':
        return f.url?.trim() ? { body: { ...n, sticker: f.url.trim() } } : { error: 'Enter the sticker image URL' };
      case 'location': {
        const lat = Number(f.lat);
        const lng = Number(f.lng);
        if (!f.lat || !f.lng || Number.isNaN(lat) || Number.isNaN(lng)) return { error: 'Enter a latitude and a longitude' };
        return { body: { ...n, latitude: lat, longitude: lng, name: f.name || undefined, address: f.address || undefined } };
      }
      case 'contact':
        if (!f.fullName?.trim() || digitsOnly(f.phoneNumber).length < 8) return { error: 'Enter the contact name and phone number' };
        return {
          body: {
            ...n,
            contact: [{ fullName: f.fullName, wuid: digitsOnly(f.phoneNumber), phoneNumber: f.phoneNumber, organization: f.organization || undefined, email: f.email || undefined }],
          },
        };
      case 'poll': {
        const values = lines(f.values ?? '');
        if (!f.name?.trim() || values.length < 2) return { error: 'A poll needs a question and at least two options (one per line)' };
        return { body: { ...n, name: f.name, selectableCount: Math.min(Number(f.selectable) || 1, values.length), values } };
      }
      case 'list': {
        const rows = lines(f.rows ?? '').map((l, i) => {
          const [title, description = ''] = l.split('|').map((x) => x.trim());
          return { title, description, rowId: `row-${i + 1}` };
        });
        if (!f.title?.trim() || !f.buttonText?.trim() || !rows.length) return { error: 'A list needs a title, a button label and at least one row' };
        return {
          body: { ...n, title: f.title, description: f.description || undefined, footerText: f.footer || undefined, buttonText: f.buttonText, sections: [{ title: f.sectionTitle || f.title, rows }] },
        };
      }
      case 'buttons': {
        const b = btns.filter((x) => x.text.trim());
        if (!f.title?.trim() || !b.length) return { error: 'Buttons need a title and at least one labelled button' };
        return { body: { ...n, title: f.title, description: f.description || undefined, footer: f.footer || undefined, buttons: b.map(btnBody) } };
      }
      case 'carousel': {
        if (!f.body?.trim() || !cards.length) return { error: 'A carousel needs a body text and at least one card' };
        if (cards.some((c) => !c.body.trim() || !c.buttons.some((b) => b.text.trim()))) return { error: 'Every card needs a body and one labelled button' };
        return {
          body: {
            ...n,
            body: f.body,
            cards: cards.map((c) => ({ title: c.title || undefined, body: c.body, imageUrl: c.imageUrl || undefined, buttons: c.buttons.filter((b) => b.text.trim()).map(btnBody) })),
          },
        };
      }
      case 'status': {
        if (!f.content?.trim()) return { error: f.stype === 'text' ? 'Write the status text' : 'Enter the media URL' };
        const everyone = f.allContacts === 'true';
        const jids = lines(f.jids ?? '').map(digitsOnly);
        if (!everyone && !jids.length) return { error: 'Choose who sees the status, or tick all contacts' };
        return {
          body: {
            type: f.stype,
            content: f.content,
            caption: f.stype === 'text' ? undefined : f.caption || undefined,
            backgroundColor: f.stype === 'text' ? f.bg || '#1b8a5a' : undefined,
            font: f.stype === 'text' ? 1 : undefined,
            allContacts: everyone,
            statusJidList: everyone ? undefined : jids,
          },
        };
      }
    }
  };
  const built = build();

  const send = () => {
    if (!('body' in built)) return;
    call.mutate(
      { instance, action: def.action, body: built.body },
      {
        onSuccess: (data) => {
          setConfirm(false);
          setResult(data ?? {});
          if (data?.suppressed || data?.status === 'suppressed') toast.warning('Not sent: suppressed by the comms sandbox');
          else toast.success('Sent');
        },
        onError: (e) => {
          setConfirm(false);
          toastError(e, 'The message was not sent');
        },
      },
    );
  };

  const suppressed = !!(result?.suppressed || result?.status === 'suppressed');

  return (
    <div className="space-y-5">
      {!connected && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          No phone is linked to this line. Link a phone from the Overview tab, then come back to send.
        </p>
      )}

      <Section title="Message type">
        <select className={selectCls} value={kind} onChange={(e) => { setKind(e.target.value as Kind); setResult(null); }}>
          {KINDS.map((k) => (
            <option key={k.id} value={k.id}>{k.label}</option>
          ))}
        </select>
      </Section>

      {!isStatus && (
        <Section title="Recipient">
          <Input placeholder="Phone with country code, e.g. 233241234567" value={to} onChange={(e) => setTo(e.target.value)} />
          <select className={selectCls} value="" onChange={(e) => e.target.value && setTo(e.target.value)} disabled={groups.isLoading}>
            <option value="">{groups.isLoading ? 'Loading groups...' : groupList.length ? 'Or pick a group' : 'No groups on this line'}</option>
            {groupList.map((g) => (
              <option key={g.id} value={g.id}>{g.subject}</option>
            ))}
          </select>
        </Section>
      )}

      <Section title="Content">
        <div className="space-y-3">
          {kind === 'text' && field('text', 'Text', '', true)}
          {kind === 'media' && (
            <>
              <div>
                <Label>Media type</Label>
                <select className={selectCls} value={f.mediatype} onChange={(e) => set('mediatype', e.target.value)}>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="document">Document</option>
                  <option value="audio">Audio</option>
                </select>
              </div>
              {field('media', 'URL', 'https://')}
              {f.mediatype === 'document' && field('fileName', 'File name', 'report.pdf')}
              {f.mediatype !== 'audio' && field('caption', 'Caption (optional)')}
            </>
          )}
          {kind === 'audio' && field('url', 'Audio URL', 'https://')}
          {kind === 'ptv' && field('url', 'Video URL', 'https://')}
          {kind === 'sticker' && field('url', 'Sticker image URL', 'https://')}
          {kind === 'location' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                {field('lat', 'Latitude', '5.6037')}
                {field('lng', 'Longitude', '-0.1870')}
              </div>
              {field('name', 'Place name (optional)')}
              {field('address', 'Address (optional)')}
            </>
          )}
          {kind === 'contact' && (
            <>
              {field('fullName', 'Full name')}
              {field('phoneNumber', 'Phone', '+233241234567')}
              {field('organization', 'Organisation (optional)')}
              {field('email', 'Email (optional)')}
            </>
          )}
          {kind === 'poll' && (
            <>
              {field('name', 'Question')}
              {field('values', 'Options, one per line', '', true)}
              <div>
                <Label>Answers a person can choose</Label>
                <Input type="number" min={1} value={f.selectable} onChange={(e) => set('selectable', e.target.value)} />
              </div>
            </>
          )}
          {kind === 'list' && (
            <>
              {field('title', 'Title')}
              {field('description', 'Description (optional)')}
              {field('buttonText', 'Button label', 'View options')}
              {field('sectionTitle', 'Section title (optional)')}
              {field('rows', 'Rows, one per line as: title | description', '', true)}
              {field('footer', 'Footer (optional)')}
            </>
          )}
          {kind === 'buttons' && (
            <>
              {field('title', 'Title')}
              {field('description', 'Description (optional)')}
              {field('footer', 'Footer (optional)')}
              <Label>Buttons</Label>
              <ButtonsEditor value={btns} onChange={setBtns} max={3} />
            </>
          )}
          {kind === 'carousel' && (
            <>
              {field('body', 'Body text')}
              {cards.map((c, i) => (
                <div key={i} className="space-y-2 rounded-lg border border-gray-200 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium uppercase text-gray-500">Card {i + 1}</span>
                    {cards.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" aria-label="Remove card" onClick={() => setCards(cards.filter((_, j) => j !== i))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <Input placeholder="Title (optional)" value={c.title} onChange={(e) => setCards(cards.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
                  <Input placeholder="Card text" value={c.body} onChange={(e) => setCards(cards.map((x, j) => (j === i ? { ...x, body: e.target.value } : x)))} />
                  <Input placeholder="Image URL (optional)" value={c.imageUrl} onChange={(e) => setCards(cards.map((x, j) => (j === i ? { ...x, imageUrl: e.target.value } : x)))} />
                  <ButtonsEditor value={c.buttons} max={2} onChange={(b) => setCards(cards.map((x, j) => (j === i ? { ...x, buttons: b } : x)))} />
                </div>
              ))}
              {cards.length < 10 && (
                <Button type="button" variant="outline" size="sm" onClick={() => setCards([...cards, { title: '', body: '', imageUrl: '', buttons: [{ type: 'url', text: '', value: '' }] }])}>
                  <Plus /> Add card
                </Button>
              )}
            </>
          )}
          {kind === 'status' && (
            <>
              <div>
                <Label>Status type</Label>
                <select className={selectCls} value={f.stype} onChange={(e) => set('stype', e.target.value)}>
                  <option value="text">Text</option>
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                </select>
              </div>
              {field('content', f.stype === 'text' ? 'Text' : 'Media URL', f.stype === 'text' ? '' : 'https://', f.stype === 'text')}
              {f.stype === 'text' ? field('bg', 'Background colour (optional)', '#1b8a5a') : field('caption', 'Caption (optional)')}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="h-4 w-4 accent-green-600" checked={f.allContacts === 'true'} onChange={(e) => set('allContacts', String(e.target.checked))} />
                Show to all contacts
              </label>
              {f.allContacts !== 'true' && field('jids', 'Show only to these phones, one per line', '', true)}
            </>
          )}
        </div>
      </Section>

      {'error' in built && <p className="text-xs text-gray-500">{built.error}.</p>}

      <ActionButton action={def.action} onClick={() => setConfirm(true)} disabled={!connected || 'error' in built || call.isPending}>
        <Send /> Send
      </ActionButton>

      {result && (
        <div className={`rounded-lg border p-3 text-sm ${suppressed ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-green-200 bg-green-50 text-green-900'}`}>
          {suppressed ? 'Not delivered: the comms sandbox suppressed this send.' : `Sent${result?.key?.id ? ` (message id ${result.key.id})` : ''}.`}
        </div>
      )}

      <ConfirmDialog
        open={confirm}
        title="Send this message?"
        body={
          isStatus
            ? 'This posts a status from the linked phone. Contacts will see it for 24 hours.'
            : `This sends a ${def.label.split(' (')[0].toLowerCase()} from the linked phone to ${number || 'the recipient'}. It is logged in the send log.`
        }
        action="Send"
        pending={call.isPending}
        onConfirm={send}
        onClose={() => setConfirm(false)}
      />
    </div>
  );
};

const digitsOnly = (s?: string) => (s ?? '').replace(/\D/g, '');

export default SendTab;
